import React, { useState } from 'react';

import { ST, Location, CustomerMembership, MembershipType } from '_lib/api/apiTypes.shim';

import { Accordion } from '_lib/components/Accordion/Accordion';
import { AccordionDetails } from '_lib/components/AccordionDetail/AccordionDetail';
import { AccordionSummary } from '_lib/components/AccordionSummary/AccordionSummary';
import { Card } from '_lib/components/Card/Card';
import { CardContent } from '_lib/components/CardContent/CardContent';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { List } from '_lib/components/List/List';
import { MenuItem } from '_lib/components/MenuItem/MenuItem';
import { TextField } from '_lib/components/TextField/TextField';
import { Typography } from '_lib/components/Typography/Typography';
import { DateFormatEnum, DateUtils } from '_lib/utils/date.utils';
import { formatAddressOneLine } from '_lib/utils/helpers';

export interface MembershipListProps {
  membershipTypes?: MembershipType[];
  memberships?: CustomerMembership[];
  locations?: Location[];
  selectedLocation?: Location;
  onSetSelectedMembership?: (selectedMembership: CustomerMembership) => void;
}

export interface ExtendedMembership extends CustomerMembership {
  type?: MembershipType;
  location?: Location;
  toDateString: string | null;
  fromDateString: string | null;
  cancellationDateString: string | null;
}

/**
 * Merges membership records with related membership type and location records into a single object. Useful to reduce repetitive lookups and to make code more readable.
 * @param memberships
 * @param membershipTypes
 * @param locations
 * @returns
 */
export const extendMemberships = (
  memberships?: CustomerMembership[],
  membershipTypes?: MembershipType[],
  locations?: Location[]
): ExtendedMembership[] => {
  return (
    memberships?.map((membership) => {
      return {
        ...membership,
        type: membershipTypes?.find(
          (membershipType) => membershipType.providerId === membership.providerMembershipTypeId
        ),
        location: locations?.find((location) => location.providerId === membership.providerLocationId),
        // membership dates here are full ISO Date time strings, BUT times are "Zero'd out" for Date-Only behavior
        // Thus, we use `timezone: 'utc'` here
        toDateString: membership.to && DateUtils.format(membership.to, DateFormatEnum.DateLong, { timezone: 'utc' }),
        fromDateString:
          membership.from && DateUtils.format(membership.from, DateFormatEnum.DateLong, { timezone: 'utc' }),
        cancellationDateString:
          membership.cancellationDate &&
          DateUtils.format(membership.cancellationDate, DateFormatEnum.DateLong, { timezone: 'utc' })
      };
    }) || []
  );
};

/**
 * Sorts a list of extended membership records to the ordering spec.
 * @param memberships - All memberships, extended with related type and location.
 * @param selectedLocation Optional selected location.
 * @returns - Sorted list.
 */
export const sortMemberships = (
  memberships: ExtendedMembership[],
  selectedLocation?: Location
): ExtendedMembership[] => {
  const matchedLocationSingleMemberships: ExtendedMembership[] = [];
  const matchedLocationAllMemberships: ExtendedMembership[] = [];
  const unmatchedLocationAllMemberships: ExtendedMembership[] = [];
  const unmatchedLocationSingleMemberships: ExtendedMembership[] = [];

  memberships.forEach((m) => {
    const isMatchedLocation = selectedLocation && selectedLocation.providerId === m.providerLocationId;
    const isSingle = m.type?.locationTarget === ST.MembershipLocationTargetEnum.SingleLocation;

    if (isMatchedLocation && isSingle) {
      matchedLocationSingleMemberships.push(m);
    } else if (isMatchedLocation) {
      matchedLocationAllMemberships.push(m);
    } else if (!isSingle) {
      unmatchedLocationAllMemberships.push(m);
    } else {
      unmatchedLocationSingleMemberships.push(m);
    }
  });

  const rankedMemberships = [
    ...matchedLocationSingleMemberships,
    ...matchedLocationAllMemberships,
    ...unmatchedLocationAllMemberships,
    ...unmatchedLocationSingleMemberships
  ];

  // Rank again with active first followed by inactive.
  const isStatusActive = (m: ExtendedMembership) => m.status === ST.CustomerMembershipStatusEnum.Active;
  const isStatusNotActive = (m: ExtendedMembership) => m.status !== ST.CustomerMembershipStatusEnum.Active;
  return [...rankedMemberships.filter(isStatusActive), ...rankedMemberships.filter(isStatusNotActive)];
};

const buildSummary = (membership: ExtendedMembership) => {
  const label = membership.type ? `${membership.type.name} (${membership.type.locationTarget})` : 'Unknown Membership';
  return (
    <>
      <Typography sx={{ display: 'inline' }} variant='body1Bold' text={`${label} -`} />
      &nbsp;
      <Typography
        color={membership.status === ST.CustomerMembershipStatusEnum.Active ? 'success' : 'error'}
        sx={{ display: 'inline' }}
        variant='body1Bold'
        text={membership.status}
      />
    </>
  );
};

const buildDetailLine = (label: string, value: string | number | null = '') => {
  return (
    <>
      <Typography sx={{ display: 'inline' }} variant='body1Bold' text={`${label} -`} />
      &nbsp;
      <Typography sx={{ display: 'inline', whiteSpace: 'pre-line' }} text={value?.toString()} />
      <br />
    </>
  );
};

const allLocationsId = 'ALL';

const filterMemberships = (memberships: ExtendedMembership[], filterLocationId: string): ExtendedMembership[] => {
  if (!memberships) return [];
  if (filterLocationId === allLocationsId) return [...memberships];
  return memberships.filter(
    (membership) =>
      membership.providerLocationId === filterLocationId ||
      membership.type?.locationTarget === ST.MembershipLocationTargetEnum.AllLocations
  );
};

export const MembershipList = (props: MembershipListProps): React.ReactElement => {
  const { locations, memberships, membershipTypes, selectedLocation } = props;

  const [filterLocationId, setFilterLocationId] = useState<string | undefined>(selectedLocation?.providerId);
  const [prevSelectedLocationId, setPrevSelectedLocationId] = useState(selectedLocation?.providerId);

  // If we change the selected location elsewhere, override the current filter.
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  if (prevSelectedLocationId !== selectedLocation?.providerId) {
    // NOTE -> setting state inside of a component body like this forces an immediate re-render!
    setPrevSelectedLocationId(selectedLocation?.providerId);
    setFilterLocationId(selectedLocation?.providerId);
  }

  const sortedMemberships = sortMemberships(
    extendMemberships(memberships, membershipTypes, locations),
    selectedLocation
  );

  const filteredMemberships = filterMemberships(sortedMemberships, filterLocationId || allLocationsId);

  return (
    <FlexBox sx={{ flexDirection: 'column' }}>
      <FlexBox>
        <TextField
          fullWidth
          label='Filter by Location'
          value={filterLocationId || allLocationsId}
          disabled={!locations || locations.length <= 1}
          select
          onChange={(e) => {
            setFilterLocationId(e.target.value);
          }}>
          <MenuItem key={'location-all'} value={allLocationsId}>
            <Typography sx={{ whiteSpace: 'pre-line' }} text='Every Location' />
          </MenuItem>
          {locations &&
            locations.map((location: Location) => (
              <MenuItem key={`location-${location.providerId}`} value={location.providerId}>
                <Typography
                  sx={{ whiteSpace: 'pre-line' }}
                  text={`${location.name} - ${formatAddressOneLine(location)}`}
                />
              </MenuItem>
            ))}
        </TextField>
      </FlexBox>
      {filteredMemberships.length === 0 && (
        <Card variant='outlined'>
          <CardContent>
            <Typography text='No membership for selected location.' />
          </CardContent>
        </Card>
      )}
      <List>
        {filteredMemberships.map((membership) => {
          return (
            <Accordion key={membership.providerId} data-testid={'membership-list-item'}>
              <AccordionSummary>{buildSummary(membership)}</AccordionSummary>
              <AccordionDetails>
                {membership.location && buildDetailLine('Location', formatAddressOneLine(membership.location))}
                {membership.fromDateString && buildDetailLine('Start', membership.fromDateString)}
                {membership.toDateString && buildDetailLine('End', membership.toDateString)}
                {membership.duration && buildDetailLine('Duration', `${membership.duration} months`)}
                {membership.billingFrequency && buildDetailLine('Billing Frequency', membership.billingFrequency)}
                {membership.renewalDuration &&
                  buildDetailLine('Renewal Duration', `${membership.renewalDuration} months`)}
                {membership.renewalBillingFrequency &&
                  buildDetailLine('Renewal Billing Frequency', membership.renewalBillingFrequency)}
                {membership.cancellationDateString && buildDetailLine('Cancelled', membership.cancellationDateString)}
                {membership.note && buildDetailLine('Note', membership.note)}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </List>
    </FlexBox>
  );
};
